import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";

export default function InsightPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(null);
    setMissing(false);
    api
      .get(`/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setMissing(true));
  }, [slug]);

  if (missing)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center" data-testid="post-not-found">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-500">404 — insight not found</p>
        <Link to="/insights" data-testid="post-back-link" className="font-mono text-xs uppercase tracking-[0.2em] text-neon link-underline">
          Back to insights
        </Link>
      </div>
    );

  if (!post)
    return (
      <div className="min-h-screen pt-40" data-testid="post-loading">
        <div className="mx-auto max-w-3xl space-y-4 px-6">
          <div className="h-8 w-40 animate-pulse rounded bg-surface" />
          <div className="h-14 w-full animate-pulse rounded bg-surface" />
          <div className="h-64 w-full animate-pulse rounded bg-surface" />
        </div>
      </div>
    );

  return (
    <>
      <SEO title={`${post.title} — Vector Growth and Advisory`} description={post.excerpt} />
      <article className="pt-40 pb-24 md:pt-48" data-testid="post-article">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <Link to="/insights" data-testid="post-back-link" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-neon">
              <ArrowLeft className="h-3.5 w-3.5" /> All insights
            </Link>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.24em] text-neon/80">{post.category}</p>
            <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl" data-testid="post-title">
              {post.title}
            </h1>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="post-meta">
              {post.date} · {post.read_time}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="clip-frame mt-10 overflow-hidden border hairline">
              <img src={post.cover} alt={post.title} className="h-72 w-full object-cover sm:h-96" data-testid="post-cover" />
            </div>
          </Reveal>

          <div className="mt-12 space-y-6" data-testid="post-body">
            {post.body.map((b, i) => {
              if (b.type === "h2")
                return (
                  <h2 key={i} className="pt-6 font-display text-2xl font-bold tracking-tight text-ink">{b.text}</h2>
                );
              if (b.type === "quote")
                return (
                  <blockquote key={i} className="border-l-2 border-neon/60 pl-6 font-display text-xl font-medium italic text-slate-700">
                    "{b.text}"
                  </blockquote>
                );
              if (b.type === "li")
                return (
                  <div key={i} className="flex items-start gap-3 text-base leading-relaxed text-slate-600">
                    <span className="mt-2.5 h-px w-5 shrink-0 bg-neon" /> {b.text}
                  </div>
                );
              return (
                <p key={i} className="text-base leading-relaxed text-slate-600">{b.text}</p>
              );
            })}
          </div>

          <Reveal className="mt-16">
            <div className="rounded-2xl border hairline bg-surface/70 p-8 text-center md:p-12" data-testid="post-cta">
              <Eyebrow>Next step</Eyebrow>
              <h3 className="mt-4 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
                Want this engineered for your pipeline?
              </h3>
              <Link
                to="/contact"
                data-testid="post-cta-button"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-mono text-xs uppercase tracking-[0.22em] text-ink transition-all duration-300 hover:shadow-[0_0_45px_rgba(47,168,224,0.45)]"
              >
                Schedule Advisory Call
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </article>
    </>
  );
}
